import { useEffect, useMemo, useRef, useState } from "react";
import PlanBoardCalendar from "./PlanBoardCalendar";
import "./plan-board.css";

const columns = [
  ["todo", "A fazer"],
  ["progress", "Em andamento"],
  ["waiting", "Aguardando"],
  ["attention", "Atenção"],
  ["done", "Concluído"],
];
const priorities = [
  ["low", "Baixa"],
  ["normal", "Normal"],
  ["high", "Alta"],
  ["critical", "Crítica"],
];
const blank = {
  title: "",
  description: "",
  categoryId: "",
  clientId: "",
  unitId: "",
  ownerId: "",
  status: "todo",
  priority: "normal",
  dueDate: "",
  nextStep: "",
};
const dateOnly = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const dateDiff = (from, to) =>
  Math.round(
    (Date.parse(`${to}T12:00:00`) - Date.parse(`${from}T12:00:00`)) / 86400000,
  );
const readTime = (value) =>
  value
    ? new Date(
        String(value).includes("T")
          ? value
          : `${String(value).replace(" ", "T")}Z`,
      )
    : null;
const monthOf = (value) => {
  const date = readTime(value);
  return date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    : "";
};
const relativeTime = (value) => {
  const date = readTime(value);
  if (!date || Number.isNaN(date.getTime())) return "Agora";
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (mins < 2) return "Agora";
  if (mins < 60) return `Há ${mins} min`;
  if (mins < 24 * 60) return `Há ${Math.floor(mins / 60)}h`;
  const days = Math.floor(mins / 1440);
  return days === 1 ? "Ontem" : `Há ${days} dias`;
};
const attention = (item) => {
  if (item.status === "done")
    return { overdue: false, near: false, stale: false, attention: false };
  const left = item.dueDate ? dateDiff(dateOnly(), item.dueDate) : null;
  const overdue = left !== null && left < 0;
  const near = left !== null && left >= 0 && left <= 3;
  const dueToday = left === 0;
  const stale =
    item.status === "progress" &&
    Date.now() - (readTime(item.updatedAt)?.getTime() || Date.now()) >=
      7 * 86400000;
  return {
    overdue,
    near,
    stale,
    attention:
      overdue ||
      dueToday ||
      stale ||
      Boolean(item.blocked) ||
      item.status === "attention",
  };
};
const dueLabel = (item) => {
  if (!item.dueDate) return "Sem prazo";
  const left = dateDiff(dateOnly(), item.dueDate);
  if (item.status !== "done" && left < 0)
    return `Atrasado ${Math.abs(left)} ${Math.abs(left) === 1 ? "dia" : "dias"}`;
  const label = new Date(`${item.dueDate}T12:00:00`)
    .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    .replace(".", "")
    .toUpperCase();
  return label;
};
const deadlineProgress = (item) => {
  if (!item.dueDate || item.status === "done") return null;
  const today = dateOnly();
  const remaining = dateDiff(today, item.dueDate);
  const created = String(item.createdAt || today).slice(0, 10);
  const total = Math.max(1, dateDiff(created, item.dueDate) + 1);
  const elapsed = Math.max(0, dateDiff(created, today) + 1);
  return {
    remaining,
    percent: Math.min(100, Math.round((elapsed / total) * 100)),
    label:
      remaining < 0
        ? `Atrasado ${Math.abs(remaining)} ${remaining === -1 ? "dia" : "dias"}`
        : remaining === 0
          ? "Entrega hoje"
          : `Faltam ${remaining} ${remaining === 1 ? "dia" : "dias"}`,
  };
};
const sortScore = (item) => {
  const flags = attention(item);
  return flags.overdue
    ? 5
    : item.priority === "critical"
      ? 4
      : flags.near
        ? 3
        : item.priority === "high"
          ? 2
          : 1;
};
const dueSortValue = (item) =>
  item.status !== "done" && item.dueDate
    ? dateDiff(dateOnly(), item.dueDate)
    : Number.MAX_SAFE_INTEGER;
const isDueAttention = (item) =>
  item.status !== "done" &&
  Boolean(item.dueDate) &&
  dateDiff(dateOnly(), item.dueDate) <= 0;
const boardStatus = (item) =>
  isDueAttention(item) ? "attention" : item.status;
const isCommercial = (item) =>
  String(item.categoryName || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("pt-BR") === "comercial";

export default function PlanBoardPage({ token, user, permissions, theme, onToggleTheme }) {
  const dark = theme === "dark";
  const boardRef = useRef(null);
  const [videowall, setVideowall] = useState(false);
  const [calendarWall, setCalendarWall] = useState(false);
  const [view, setView] = useState("board");
  const [data, setData] = useState({
    items: [],
    activities: [],
    categories: [],
    users: [],
    clients: [],
    units: [],
  });
  const [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [form, setForm] = useState(blank),
    [creating, setCreating] = useState(false),
    [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null),
    [detail, setDetail] = useState(null);
  const [updateText, setUpdateText] = useState(""),
    [activityText, setActivityText] = useState("");
  const [search, setSearch] = useState(""),
    [ownerFilter, setOwnerFilter] = useState(""),
    [categoryFilter, setCategoryFilter] = useState(""),
    [priorityFilter, setPriorityFilter] = useState(""),
    [deadlineFilter, setDeadlineFilter] = useState("");
  const [quickFilter, setQuickFilter] = useState("all"),
    [kpiFilter, setKpiFilter] = useState("");
  const [compact, setCompact] = useState(
    () => localStorage.getItem(`plan-board-compact-${user.id}`) === "1",
  );
  const [orderMode, setOrderMode] = useState("smart"),
    [menuId, setMenuId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [clockTick, setClockTick] = useState(0);
  const canCreate = permissions.includes("plan_board.create"),
    canEdit = permissions.includes("plan_board.edit"),
    canDelete = permissions.includes("plan_board.delete"),
    canAdmin = permissions.includes("plan_board.admin");
  const request = async (path, method = "GET", body) => {
    const response = await fetch(`/api/plan-board${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response.ok)
      throw new Error(
        (await response.json().catch(() => ({}))).error ||
          "Não foi possível concluir a operação.",
      );
    return response.status === 204 ? null : response.json();
  };
  const load = async () => {
    try {
      setData(await request(""));
      setError("");
    } catch {
      setError("Não foi possível carregar o PLAN Board.");
    } finally {
      setLoading(false);
    }
  };
  const loadDetail = async (id) => {
    try {
      setDetail(await request(`/items/${id}`));
    } catch {
      setError("Não foi possível carregar o assunto.");
    }
  };
  useEffect(() => {
    load();
  }, [token]);
  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId]);
  useEffect(() => {
    const timer = setInterval(() => setClockTick((value) => value + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    localStorage.setItem(`plan-board-compact-${user.id}`, compact ? "1" : "0");
  }, [compact, user.id]);
  useEffect(() => {
    const syncFullscreen = () => {
      if (document.fullscreenElement !== boardRef.current) {
        setVideowall(false);
        setCalendarWall(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === "Escape" && !document.fullscreenElement) {
        setVideowall(false);
        setCalendarWall(false);
      }
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("keydown", onEscape);
    };
  }, []);
  useEffect(() => {
    if (!videowall && !calendarWall) return;
    let active = true;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing || document.visibilityState === "hidden") return;
      refreshing = true;
      try {
        const next = await request("");
        if (active)
          setData((current) =>
            JSON.stringify(current) === JSON.stringify(next) ? current : next,
          );
      } catch {
        // Uma falha temporária mantém o último quadro visível até a próxima tentativa.
      } finally {
        refreshing = false;
      }
    };
    const timer = window.setInterval(refresh, 20000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [videowall, calendarWall, token]);
  const toggleVideowall = async () => {
    if (videowall) {
      if (document.fullscreenElement === boardRef.current)
        await document.exitFullscreen().catch(() => {});
      setVideowall(false);
      return;
    }
    setVideowall(true);
    if (boardRef.current?.requestFullscreen)
      await boardRef.current.requestFullscreen().catch(() => {});
  };
  const toggleCalendarWall = async () => {
    if (calendarWall) {
      if (document.fullscreenElement === boardRef.current)
        await document.exitFullscreen().catch(() => {});
      setCalendarWall(false);
      return;
    }
    setCalendarWall(true);
    if (boardRef.current?.requestFullscreen)
      await boardRef.current.requestFullscreen().catch(() => {});
  };
  const mutate = async (item, patch) => {
    if (!canEdit) return;
    try {
      const changed = await request(`/items/${item.id}`, "PATCH", patch);
      setData((old) => ({
        ...old,
        items: old.items.map((row) => (row.id === item.id ? changed : row)),
      }));
      if (selectedId === item.id) await loadDetail(item.id);
      setError("");
    } catch {
      setError("Não foi possível salvar a alteração.");
    }
  };
  const completeItem = async (item) => {
    if (!canEdit || item.status === "done" || completingId === item.id) return;
    setCompletingId(item.id);
    try {
      await mutate(item, { status: "done" });
    } finally {
      setCompletingId(null);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const item = await request("/items", "POST", {
        ...form,
        categoryId: form.categoryId || null,
        clientId: form.clientId || null,
        unitId: form.unitId || null,
        ownerId: form.ownerId || null,
        dueDate: form.dueDate || null,
      });
      setData((old) => ({ ...old, items: [...old.items, item] }));
      setCreating(false);
      setForm(blank);
      setError("");
    } catch {
      setError("Não foi possível criar o assunto.");
    } finally {
      setSaving(false);
    }
  };
  const sendUpdate = async () => {
    if (!updateText.trim() || !selectedId) return;
    try {
      await request(`/items/${selectedId}/updates`, "POST", {
        content: updateText.trim(),
      });
      setUpdateText("");
      await Promise.all([loadDetail(selectedId), load()]);
    } catch {
      setError("Não foi possível registrar a atualização.");
    }
  };
  const addActivity = async () => {
    if (!activityText.trim() || !selectedId) return;
    try {
      await request(`/items/${selectedId}/activities`, "POST", {
        description: activityText.trim(),
      });
      setActivityText("");
      await loadDetail(selectedId);
      await load();
    } catch {
      setError("Não foi possível adicionar a atividade.");
    }
  };
  const toggleActivity = async (activity) => {
    try {
      await request(`/activities/${activity.id}`, "PATCH", {
        completed: !activity.completed,
      });
      await loadDetail(selectedId);
      await load();
    } catch {
      setError("Não foi possível atualizar a atividade.");
    }
  };
  const editActivity = async (activity, patch) => {
    try {
      await request(`/activities/${activity.id}`, "PATCH", patch);
      await loadDetail(selectedId);
      await load();
    } catch {
      setError("Não foi possível atualizar a atividade.");
    }
  };
  const createCategory = async () => {
    const name = window.prompt("Nome da categoria");
    if (!name?.trim()) return;
    try {
      const category = await request("/categories", "POST", {
        name: name.trim(),
      });
      setData((old) => ({
        ...old,
        categories: [...old.categories, category].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      }));
      setForm((old) => ({ ...old, categoryId: String(category.id) }));
    } catch {
      setError("Não foi possível criar a categoria.");
    }
  };
  const remove = async (item) => {
    if (!window.confirm(`Excluir “${item.title}” e seu histórico?`)) return;
    try {
      await request(`/items/${item.id}`, "DELETE");
      setData((old) => ({
        ...old,
        items: old.items.filter((row) => row.id !== item.id),
      }));
      setSelectedId(null);
    } catch {
      setError("Não foi possível excluir o assunto.");
    }
  };
  const visible = useMemo(
    () =>
      data.items.filter((item) => {
        if (videowall) return item.status !== "done";
        const flags = attention(item),
          q = search.trim().toLocaleLowerCase("pt-BR");
        if (
          q &&
          ![
            item.title,
            item.clientName,
            item.unitName,
            item.categoryName,
            item.ownerName,
            item.nextStep,
            item.description,
          ].some((value) =>
            String(value || "")
              .toLocaleLowerCase("pt-BR")
              .includes(q),
          )
        )
          return false;
        if (ownerFilter && String(item.ownerId) !== ownerFilter) return false;
        if (categoryFilter && String(item.categoryId) !== categoryFilter)
          return false;
        if (priorityFilter && item.priority !== priorityFilter) return false;
        if (deadlineFilter === "overdue" && !flags.overdue) return false;
        if (deadlineFilter === "near" && !flags.near) return false;
        if (deadlineFilter === "none" && item.dueDate) return false;
        if (quickFilter === "mine" && Number(item.ownerId) !== Number(user.id))
          return false;
        if (quickFilter === "overdue" && !flags.overdue) return false;
        if (quickFilter === "waiting" && boardStatus(item) !== "waiting")
          return false;
        if (quickFilter === "attention" && !flags.attention) return false;
        if (kpiFilter === "open" && item.status === "done") return false;
        if (
          kpiFilter === "proposals" &&
          (!isCommercial(item) || item.status === "done")
        )
          return false;
        if (kpiFilter === "attention" && !flags.attention) return false;
        if (kpiFilter === "overdue" && !flags.overdue) return false;
        if (kpiFilter === "waiting" && boardStatus(item) !== "waiting")
          return false;
        if (
          kpiFilter === "done" &&
          !(
            item.status === "done" &&
            monthOf(item.completedAt) === dateOnly().slice(0, 7)
          )
        )
          return false;
        return true;
      }),
    [
      data.items,
      search,
      ownerFilter,
      categoryFilter,
      priorityFilter,
      deadlineFilter,
      quickFilter,
      kpiFilter,
      user.id,
      clockTick,
      videowall,
    ],
  );
  const kpis = [
    ["open", "Em aberto", data.items.filter((i) => i.status !== "done").length],
    [
      "proposals",
      "Propostas",
      data.items.filter((i) => isCommercial(i) && i.status !== "done").length,
    ],
    [
      "attention",
      "Atenção",
      data.items.filter((i) => attention(i).attention).length,
    ],
    [
      "overdue",
      "Atrasados",
      data.items.filter((i) => attention(i).overdue).length,
    ],
    [
      "waiting",
      "Aguardando",
      data.items.filter((i) => boardStatus(i) === "waiting").length,
    ],
    [
      "done",
      "Concluídos este mês",
      data.items.filter(
        (i) =>
          i.status === "done" &&
          monthOf(i.completedAt) === dateOnly().slice(0, 7),
      ).length,
    ],
  ];
  const sorted = (items) =>
    [...items].sort(
      (a, b) =>
        Number(isCommercial(b)) - Number(isCommercial(a)) ||
        (orderMode === "manual" && !videowall
          ? Number(a.sortOrder) - Number(b.sortOrder)
          : dueSortValue(a) - dueSortValue(b) ||
            sortScore(b) - sortScore(a) ||
            (readTime(b.updatedAt)?.getTime() || 0) -
              (readTime(a.updatedAt)?.getTime() || 0)),
    );
  const onDrop = (event, status) => {
    event.preventDefault();
    const id = Number(event.dataTransfer.getData("text/plain"));
    const item = data.items.find((row) => row.id === id);
    if (item && isDueAttention(item) && status !== "attention") {
      setError(
        "Assuntos que vencem hoje ou estão atrasados permanecem em Atenção até alterar o prazo ou concluir.",
      );
      return;
    }
    if (item && item.status !== status) mutate(item, { status });
  };
  const nudge = (item, direction) => {
    const rows = [...data.items]
      .filter(
        (row) =>
          boardStatus(row) === boardStatus(item) &&
          isCommercial(row) === isCommercial(item),
      )
      .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    const index = rows.findIndex((row) => row.id === item.id);
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const left = direction < 0 ? rows[target - 1] : rows[target];
    const right = direction < 0 ? rows[target] : rows[target + 1];
    mutate(item, {
      sortOrder:
        left && right
          ? (Number(left.sortOrder) + Number(right.sortOrder)) / 2
          : left
            ? Number(left.sortOrder) + 1
            : Number(right.sortOrder) - 1,
    });
  };
  const label = (options, value) =>
    options.find(([key]) => key === value)?.[1] || "";
  const shownColumns = videowall
    ? columns.filter(([status]) => status !== "done")
    : columns;
  const wallGrid =
    "minmax(500px, 2.1fr) minmax(220px, 1fr) minmax(200px, 0.9fr) minmax(250px, 1fr)";
  const compactCards = compact || videowall;

  return (
    <div
      ref={boardRef}
      className={`plan-board-page${dark ? " dark" : ""}${compactCards ? " compact" : " director-mode"}${videowall ? " is-videowall" : ""}${calendarWall ? " is-calendar-wall" : ""}`}
    >
      <div className="pb-hero">
        <div className="pb-hero-brand">
          {videowall && (
            <img
              className="pb-wall-logo"
              src={
                dark
                  ? "/aboutplan-logo-white-hq.png"
                  : "/aboutplan-horizontal-hq.png"
              }
              alt="AboutPlan — Gestão Inteligente"
            />
          )}
          <div>
            <span className="pb-eyebrow">VISÃO GERAL DA DIRETORIA</span>
            <h2>PLAN Board</h2>
            <p>Assuntos, decisões e próximos passos em uma única visão.</p>
          </div>
        </div>
        <div className="pb-hero-actions">
          {videowall && (
            <span className="pb-wall-sync">Atualização automática · 20 s</span>
          )}
          {(view === "board" || videowall) && (
            <button
              type="button"
              className="pb-videowall-toggle"
              onClick={toggleVideowall}
              aria-pressed={videowall}
            >
              {videowall ? "Sair do videowall" : "Modo videowall"}
            </button>
          )}
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={dark ? "Ativar modo claro" : "Ativar modo escuro"}
          >
            {dark ? "☀ Modo claro" : "◐ Modo escuro"}
          </button>
          {canCreate && (
            <button className="pb-primary" onClick={() => setCreating(true)}>
              + Novo assunto
            </button>
          )}
        </div>
      </div>
      {error && (
        <div className="pb-error" role="alert">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}
      <div className="pb-kpis">
        {kpis
          .filter(([key]) => !videowall || key !== "done")
          .map(([key, name, count]) => (
            <button
              key={key}
              className={`pb-kpi pb-kpi-${key}${!videowall && kpiFilter === key ? " selected" : ""}`}
              aria-pressed={!videowall && kpiFilter === key}
              disabled={videowall}
              onClick={() => setKpiFilter(kpiFilter === key ? "" : key)}
            >
              <span className="pb-kpi-label">{name}</span>
              <strong>{count}</strong>
            </button>
          ))}
      </div>
      <div className="pb-toolbar">
        <div className="pb-filters">
          <label className="pb-search">
            <span aria-hidden="true">⌕</span>
            <input
              aria-label="Pesquisar assuntos"
              placeholder="Pesquisar assuntos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            aria-label="Filtrar responsável"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
          >
            <option value="">Responsável</option>
            {data.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar categoria"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Categoria</option>
            {data.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar prioridade"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">Prioridade</option>
            {priorities.map(([value, name]) => (
              <option key={value} value={value}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar prazo"
            value={deadlineFilter}
            onChange={(e) => setDeadlineFilter(e.target.value)}
          >
            <option value="">Prazo</option>
            <option value="overdue">Atrasado</option>
            <option value="near">Próximo</option>
            <option value="none">Sem prazo</option>
          </select>
        </div>
        <div className="pb-shortcuts">
          {[
            ["all", "Todos"],
            ["mine", "Meus"],
            ["overdue", "Atrasados"],
            ["waiting", "Aguardando"],
            ["attention", "Atenção"],
          ].map(([key, name]) => (
            <button
              key={key}
              className={quickFilter === key ? "active" : ""}
              onClick={() => setQuickFilter(key)}
            >
              {name}
            </button>
          ))}
        </div>
        <div className="pb-view-options">
          <div className="pb-segment pb-layout-switch" role="group" aria-label="Visualização do Plan Board">
            <button type="button" className={view === "board" ? "active" : ""} aria-pressed={view === "board"} onClick={() => setView("board")}>Quadro</button>
            <button type="button" className={view === "calendar" ? "active" : ""} aria-pressed={view === "calendar"} onClick={() => setView("calendar")} title="Calendário de assuntos e atividades"><span aria-hidden="true">▦</span> Calendário</button>
          </div>
          {view === "board" && <>
          <select
            aria-label="Ordenação"
            value={orderMode}
            onChange={(e) => setOrderMode(e.target.value)}
          >
            <option value="smart">Ordem inteligente</option>
            <option value="manual">Ordem manual</option>
          </select>
          <div className="pb-segment">
            <button
              className={!compact ? "active" : ""}
              aria-pressed={!compact}
              onClick={() => setCompact(false)}
            >
              Visão Diretoria
            </button>
            <button
              className={compact ? "active" : ""}
              aria-pressed={compact}
              onClick={() => setCompact(true)}
            >
              Compacto
            </button>
          </div>
          </>}
        </div>
      </div>
      {loading ? (
        <div className="pb-empty">Carregando assuntos…</div>
      ) : view === "calendar" && !videowall ? (
        <PlanBoardCalendar items={visible} activities={data.activities} onOpen={setSelectedId} videowall={calendarWall} onToggleVideowall={toggleCalendarWall} />
      ) : (
        <div
          className="pb-columns"
          style={videowall ? { gridTemplateColumns: wallGrid } : undefined}
        >
          {shownColumns.map(([status, name]) => {
            const items = sorted(
              visible.filter((item) => boardStatus(item) === status),
            );
            return (
              <section
                key={status}
                className={`pb-column status-${status}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => onDrop(event, status)}
              >
                <header>
                  <h3>{name}</h3>
                  <span>{items.length}</span>
                </header>
                <div className="pb-column-body">
                  {items.map((item) => {
                    const flags = attention(item);
                    const deadline = deadlineProgress(item);
                    return (
                      <article
                        key={item.id}
                        className={`pb-card status-${status}${isCommercial(item) ? " category-commercial" : ""}${isDueAttention(item) ? " due-attention" : ""}${flags.overdue ? " overdue" : ""}${canEdit && item.status !== "done" ? " can-complete" : ""}${!deadline ? " no-deadline" : ""}`}
                        draggable={canEdit}
                        onDragStart={(event) =>
                          event.dataTransfer.setData(
                            "text/plain",
                            String(item.id),
                          )
                        }
                        onClick={() => setSelectedId(item.id)}
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (
                            event.target === event.currentTarget &&
                            event.key === "Enter"
                          )
                            setSelectedId(item.id);
                        }}
                      >
                        <div className="pb-card-top">
                          <span className="pb-card-location" title={[item.clientName, item.unitName].filter(Boolean).join(" · ") || item.categoryName || "Assunto interno"}>
                            <b>{item.clientName || item.categoryName || "Assunto interno"}</b>
                            {item.clientName && item.unitName && <small>{item.unitName}</small>}
                          </span>
                          {!compactCards && item.priority !== "normal" && (
                            <em>{label(priorities, item.priority)}</em>
                          )}
                          {canEdit && (
                            <button
                              className="pb-more"
                              title="Ações do assunto"
                              onClick={(event) => {
                                event.stopPropagation();
                                setMenuId(menuId === item.id ? null : item.id);
                              }}
                            >
                              ⋯
                            </button>
                          )}
                        </div>
                        <h4 title={compactCards ? item.title : undefined}>
                          {item.title}
                        </h4>
                        {!compactCards && item.nextStep && (
                          <div className="pb-next">
                            <small>Próximo passo</small>
                            <p>{item.nextStep}</p>
                          </div>
                        )}
                        {!compactCards && !item.nextStep && (
                          <div className="pb-next muted">
                            Próximo passo não definido
                          </div>
                        )}
                        <div className="pb-card-foot">
                          {!compactCards && (
                            <span>{item.ownerName || "Sem responsável"}</span>
                          )}
                          <span className={flags.overdue ? "late" : ""}>
                            {dueLabel(item)}
                          </span>
                        </div>
                        {deadline && (
                          <div
                            className={`pb-deadline${deadline.remaining < 0 ? " late" : deadline.remaining <= 3 ? " near" : ""}`}
                          >
                            <div className="pb-deadline-meta">
                              <span>Prazo de entrega</span>
                              <strong>{deadline.label}</strong>
                            </div>
                            <div
                              className="pb-deadline-track"
                              role="progressbar"
                              aria-label="Prazo de entrega"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={deadline.percent}
                              aria-valuetext={deadline.label}
                            >
                              <span style={{ width: `${deadline.percent}%` }} />
                            </div>
                          </div>
                        )}
                        {canEdit && item.status !== "done" && (
                          <button
                            type="button"
                            className="pb-complete"
                            title="Concluir assunto"
                            aria-label={`Concluir assunto: ${item.title}`}
                            disabled={completingId === item.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              completeItem(item);
                            }}
                          >
                            <svg
                              viewBox="0 0 20 20"
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="m4.5 10 3.5 3.5 7.5-7.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        )}
                        {menuId === item.id && (
                          <div
                            className="pb-card-menu"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setSelectedId(item.id);
                                setMenuId(null);
                              }}
                            >
                              Editar
                            </button>
                            {orderMode === "manual" && !videowall && (
                              <>
                                <button
                                  onClick={() => {
                                    nudge(item, -1);
                                    setMenuId(null);
                                  }}
                                >
                                  Mover para cima
                                </button>
                                <button
                                  onClick={() => {
                                    nudge(item, 1);
                                    setMenuId(null);
                                  }}
                                >
                                  Mover para baixo
                                </button>
                              </>
                            )}
                            {item.status !== "done" && (
                              <button
                                onClick={() => {
                                  completeItem(item);
                                  setMenuId(null);
                                }}
                              >
                                Concluir
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => {
                                  remove(item);
                                  setMenuId(null);
                                }}
                              >
                                Excluir
                              </button>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                  {!items.length && (
                    <p className="pb-empty-column">Sem assuntos nesta etapa</p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {creating && (
        <div
          className="pb-overlay"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && event.button === 0)
              setCreating(false);
          }}
        >
          <form
            className="pb-create"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submit}
          >
            <header>
              <div>
                <span className="pb-eyebrow">CADASTRO RÁPIDO</span>
                <h3>Novo assunto</h3>
              </div>
              <button type="button" onClick={() => setCreating(false)}>
                ×
              </button>
            </header>
            {error && (
              <p className="pb-form-error" role="alert">
                {error}
              </p>
            )}
            <label>
              Assunto *
              <input
                autoFocus
                required
                maxLength={180}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="O que precisa de acompanhamento?"
              />
            </label>
            <div className="pb-form-grid">
              <label>
                Categoria{" "}
                <span className="pb-inline">
                  <select
                    value={form.categoryId}
                    onChange={(e) =>
                      setForm({ ...form, categoryId: e.target.value })
                    }
                  >
                    <option value="">Sem categoria</option>
                    {data.categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {canAdmin && (
                    <button
                      type="button"
                      title="Criar categoria"
                      onClick={createCategory}
                    >
                      +
                    </button>
                  )}
                </span>
              </label>
              <label>
                Responsável
                <select
                  value={form.ownerId}
                  onChange={(e) =>
                    setForm({ ...form, ownerId: e.target.value })
                  }
                >
                  <option value="">Não definido</option>
                  {data.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Cliente
                <select
                  value={form.clientId}
                  onChange={(e) =>
                    setForm({ ...form, clientId: e.target.value, unitId: "" })
                  }
                >
                  <option value="">Assunto interno</option>
                  {data.clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Unidade
                <select
                  disabled={!form.clientId}
                  value={form.unitId}
                  onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                >
                  <option value="">Não definida</option>
                  {data.units
                    .filter((u) => String(u.clientId) === String(form.clientId))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {columns.map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Prioridade
                <select
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  {priorities.map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Prazo
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({ ...form, dueDate: e.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Próximo passo
              <input
                maxLength={500}
                value={form.nextStep}
                onChange={(e) => setForm({ ...form, nextStep: e.target.value })}
                placeholder="A próxima ação concreta"
              />
            </label>
            <label>
              Descrição (opcional)
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </label>
            <footer>
              <button type="button" onClick={() => setCreating(false)}>
                Cancelar
              </button>
              <button className="pb-primary" disabled={saving}>
                {saving ? "Criando…" : "Criar assunto"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {selectedId && detail && (
        <div
          className="pb-drawer-overlay"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && event.button === 0)
              setSelectedId(null);
          }}
        >
          <aside
            className="pb-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <header>
              <span className="pb-eyebrow">ASSUNTO</span>
              <button
                onClick={() => setSelectedId(null)}
                aria-label="Fechar assunto"
              >
                ×
              </button>
            </header>
            <h2>
              <input
                className="pb-title-edit"
                aria-label="Título do assunto"
                disabled={!canEdit}
                key={`${detail.item.id}-${detail.item.title}`}
                defaultValue={detail.item.title}
                onBlur={(e) => {
                  const title = e.target.value.trim();
                  if (title && title !== detail.item.title)
                    mutate(detail.item, { title });
                }}
              />
            </h2>
            <p className="pb-drawer-context">
              {detail.item.clientName
                ? `${detail.item.clientName}${detail.item.unitName ? ` → ${detail.item.unitName}` : ""}`
                : detail.item.categoryName || "Assunto interno"}
            </p>
            {error && (
              <p className="pb-form-error" role="alert">
                {error}
              </p>
            )}
            <div className="pb-drawer-fields">
              <label>
                Status
                {isDueAttention(detail.item) && (
                  <small className="pb-auto-attention">
                    Atenção automática: prazo em até 1 dia
                  </small>
                )}
                <select
                  disabled={!canEdit}
                  value={boardStatus(detail.item)}
                  onChange={(e) =>
                    mutate(detail.item, { status: e.target.value })
                  }
                >
                  {columns.map(([value, name]) => (
                    <option
                      key={value}
                      value={value}
                      disabled={
                        isDueAttention(detail.item) &&
                        value !== "attention" &&
                        value !== "done"
                      }
                    >
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Responsável
                <select
                  disabled={!canEdit}
                  value={detail.item.ownerId || ""}
                  onChange={(e) =>
                    mutate(detail.item, { ownerId: e.target.value || null })
                  }
                >
                  <option value="">Não definido</option>
                  {data.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Prioridade
                <select
                  disabled={!canEdit}
                  value={detail.item.priority}
                  onChange={(e) =>
                    mutate(detail.item, { priority: e.target.value })
                  }
                >
                  {priorities.map(([value, name]) => (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Prazo
                <input
                  disabled={!canEdit}
                  type="date"
                  value={detail.item.dueDate || ""}
                  onChange={(e) =>
                    mutate(detail.item, { dueDate: e.target.value || null })
                  }
                />
              </label>
              <label>
                Categoria
                <select
                  disabled={!canEdit}
                  value={detail.item.categoryId || ""}
                  onChange={(e) =>
                    mutate(detail.item, { categoryId: e.target.value || null })
                  }
                >
                  <option value="">Sem categoria</option>
                  {data.categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Cliente
                <select
                  disabled={!canEdit}
                  value={detail.item.clientId || ""}
                  onChange={(e) =>
                    mutate(detail.item, {
                      clientId: e.target.value || null,
                      unitId: null,
                    })
                  }
                >
                  <option value="">Assunto interno</option>
                  {data.clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Unidade
                <select
                  disabled={!canEdit || !detail.item.clientId}
                  value={detail.item.unitId || ""}
                  onChange={(e) =>
                    mutate(detail.item, { unitId: e.target.value || null })
                  }
                >
                  <option value="">Não definida</option>
                  {data.units
                    .filter(
                      (unit) =>
                        String(unit.clientId) === String(detail.item.clientId),
                    )
                    .map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            <label className="pb-blocked">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={Boolean(detail.item.blocked)}
                onChange={(e) =>
                  mutate(detail.item, { blocked: e.target.checked })
                }
              />{" "}
              Impedimento / bloqueado
            </label>
            <section className="pb-drawer-section">
              <h3>Próximo passo</h3>
              <textarea
                disabled={!canEdit}
                key={`${detail.item.id}-${detail.item.nextStep}`}
                defaultValue={detail.item.nextStep || ""}
                placeholder="Qual é a próxima ação?"
                onBlur={(e) => {
                  if (e.target.value !== (detail.item.nextStep || ""))
                    mutate(detail.item, { nextStep: e.target.value });
                }}
              />
            </section>
            <section className="pb-drawer-section">
              <h3>Descrição</h3>
              <textarea
                disabled={!canEdit}
                key={`${detail.item.id}-${detail.item.description}`}
                defaultValue={detail.item.description || ""}
                placeholder="Contexto opcional do assunto"
                onBlur={(e) => {
                  if (e.target.value !== (detail.item.description || ""))
                    mutate(detail.item, { description: e.target.value });
                }}
              />
            </section>
            <section className="pb-drawer-section">
              <h3>
                Atividades{" "}
                <small>
                  {detail.activities.filter((a) => a.completed).length}/
                  {detail.activities.length}
                </small>
              </h3>
              <div className="pb-activities">
                {detail.activities.map((activity) => (
                  <div className="pb-activity-row" key={activity.id}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={Boolean(activity.completed)}
                        onChange={() => toggleActivity(activity)}
                      />
                      <span className={activity.completed ? "done" : ""}>
                        {activity.description}
                      </span>
                    </label>
                    <div className="pb-activity-meta">
                      <select
                        aria-label={`Responsável por ${activity.description}`}
                        disabled={!canEdit}
                        value={activity.ownerId || ""}
                        onChange={(e) =>
                          editActivity(activity, {
                            ownerId: e.target.value || null,
                          })
                        }
                      >
                        <option value="">Sem responsável</option>
                        {data.users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                      <input
                        aria-label={`Prazo de ${activity.description}`}
                        disabled={!canEdit}
                        type="date"
                        value={activity.dueDate || ""}
                        onChange={(e) =>
                          editActivity(activity, {
                            dueDate: e.target.value || null,
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="pb-add-row">
                  <input
                    value={activityText}
                    onChange={(e) => setActivityText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addActivity();
                    }}
                    placeholder="Nova atividade"
                  />
                  <button onClick={addActivity}>Adicionar</button>
                </div>
              )}
            </section>
            <section className="pb-drawer-section">
              <h3>Atualizações</h3>
              {canEdit && (
                <div className="pb-update-box">
                  <textarea
                    value={updateText}
                    onChange={(e) => setUpdateText(e.target.value)}
                    placeholder="Adicionar atualização…"
                  />
                  <button
                    className="pb-primary"
                    disabled={!updateText.trim()}
                    onClick={sendUpdate}
                  >
                    Registrar
                  </button>
                </div>
              )}
              <div className="pb-history">
                {detail.updates.map((update) => (
                  <article key={update.id}>
                    <div>
                      <strong>{update.userName || "Equipe"}</strong>
                      <time>{relativeTime(update.createdAt)}</time>
                    </div>
                    <p>{update.content}</p>
                  </article>
                ))}
                {!detail.updates.length && (
                  <p className="pb-muted">Nenhuma atualização registrada.</p>
                )}
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
